// TODO: Refactor this file. This file is too large (Yaseen)
import Head from "next/head";
import { useRouter } from "next/router";

import { RhIcon, RhInputGroup, RhPagination, RhTable } from "@rhythm-ui/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";

import { LocationPreferenceEnum } from "@/types/candidate";
import {
  ContactDetailsType,
  InterviewStageType,
  JobFilterParamType,
  JobPrioritySkillType,
  JobType,
  OwnerDetailsType,
} from "@/types/job";
import { CityType } from "@/types/utils";

import api from "@/services/api";

import { LAKH_CURRENCY_MULTIPLIER } from "@/constants";

import formatCurrencyRange from "@/helpers/formatCurrencyRange";
import formatExperienceRange from "@/helpers/formatExperienceRange";

import { DebouncedRhInput } from "@/components/dataInput/DebouncedInput";
import NavBar from "@/components/layout/NavBar";
import { Button } from "@/components/ui/Button";
import { BoxTooltip } from "@/components/utils/Boxtooltip";
import { Chip } from "@/components/utils/Chip";

import JobDetailsDrawer from "./components/JobDetailsDrawer";
import JobFilter from "./components/JobFilter";

const defaultFilters: JobFilterParamType = {
  ordering: "-modified_ts",
  page: 1,
  status: "Active",
  is_codemonk_handpick: "false",
};

let isPageFirstLoad = true;

const Jobs = () => {
  const [openJobDrawerId, setOpenJobDrawerId] = useState<number | null>(null);

  const [filterJobsOn, setFilterJobsOn] = useState(defaultFilters);

  const router = useRouter();

  useEffect(() => {
    if (router.isReady) {
      const jobId = router.query.id;
      if (jobId && typeof jobId === "string") {
        if (isPageFirstLoad) {
          // setShowJobResultNavBar(false);
        }
        setOpenJobDrawerId(parseInt(jobId));
      } else {
        setOpenJobDrawerId(null);
      }
      isPageFirstLoad = false;
    }
  }, [router]);

  const formattedFilter = useMemo(
    () => formatFilterForApi(filterJobsOn),
    [filterJobsOn],
  );

  const { data: jobsListResponse } = useQuery({
    queryKey: ["JOBS", "LIST", formattedFilter],
    queryFn: () => api.company.getJobs(formattedFilter),
    enabled: isFilterValid(filterJobsOn),
  });

  const jobsList = jobsListResponse?.data.results;

  const jobsCount = jobsListResponse?.data.count;

  const onAllFilterClear = () => {
    setFilterJobsOn(defaultFilters);
  };

  const tableHeader = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Job Title",
        cell: ({ row: { original } }: { row: { original: JobType } }) =>
          original?.title ? (
            <>
              {original.is_draft ? (
                <p>{original.title}</p>
              ) : (
                <button
                  data-testid="jobs-tableview"
                  onClick={() => router.push(`jobs?id=${original.id}`)}
                  className="underline"
                >
                  {original.title}
                </button>
              )}
            </>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "company",
        header: "Employer Name",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {original.company?.name}
            {original.handpick_company?.name
              ? `(${original.handpick_company?.name})`
              : ""}
          </p>
        ),
      },
      {
        accessorKey: "min_exp",
        header: "Experience Range (y)",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p className="">
            {formatExperienceRange.number({
              min: original.min_exp,
              max: original.max_exp,
            }) || "-"}
          </p>
        ),
      },
      {
        accessorKey: "min_salary",
        header: "Salary Range (lpa)",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {formatCurrencyRange({
              min: original.min_salary,
              max: original.max_salary,
            })}
          </p>
        ),
      },
      {
        accessorKey: "location_new",
        header: "Location",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <PreferredCityView
            cities={original.location_new}
            work_type={original.work_preference_new}
          />
        ),
      },
      {
        accessorKey: "openings",
        header: "No. of Openings",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.openings}</p>
        ),
      },
      {
        accessorKey: "target_date",
        header: "Target Dates",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {original.target_date
              ? dayjs(original.target_date).format("DD MMM YYYY")
              : "-"}{" "}
          </p>
        ),
      },
      {
        accessorKey: "job_role",
        header: "Job Role",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.job_role?.name ?? "-"}</p>
        ),
      },
      {
        accessorKey: "job_type",
        header: "Job Type",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.job_type ?? "-"}</p>
        ),
      },
      {
        accessorKey: "negotiable_min_exp",
        header: "Negotiable Min Experience (y)",
        cell: ({ row: { original } }: { row: { original: JobType } }) =>
          <p className="">{original.negotiable_min_exp ?? "-"}</p> ?? "-",
      },

      {
        accessorKey: "joining_availability_days",
        header: "Joining Availability",
        cell: ({ row: { original } }: { row: { original: JobType } }) =>
          (
            <p className="">
              {original.joining_availability_days
                ? `Within ${original.joining_availability_days === 999 ? "Any" : original.joining_availability_days} days`
                : "-"}
            </p>
          ) ?? "-",
      },

      {
        accessorKey: "negotiable_min_joining_availability",
        header: "Negotiable Joining Availability",
        cell: ({ row: { original } }: { row: { original: JobType } }) =>
          (
            <p className="">
              {original.negotiable_min_joining_availability
                ? `Within ${original.joining_availability_days === 999 ? "Any" : original.joining_availability_days} days`
                : "-"}
            </p>
          ) ?? "-",
      },
      {
        accessorKey: "work_preference_new",
        header: "Work Type",
        cell: ({ row: { original } }: { row: { original: JobType } }) =>
          original.work_preference_new ? (
            <p className="">
              {original.work_preference_new?.join(", ") ?? "-"}
            </p>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "negotiable_max_salary",
        header: "Negotiable Max Salary (lpa)",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.negotiable_max_salary}</p>
        ),
      },
      {
        accessorKey: "priority_skills",
        header: "Prioritized Skills",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <PrioritizedSkills priority_skills={original.priority_skills} />
        ),
      },

      {
        accessorKey: "priority_skills.non_deal_breaker_skills",
        header: "Non Deal Breaker Skills",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <NonDealBreakderSkills priority_skills={original.priority_skills} />
        ),
      },

      {
        accessorKey: "is_public",
        header: "Public/ Private",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {original.is_public !== undefined
              ? original.is_public
                ? "Public"
                : "Private"
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "is_public",
        header: "Team Members",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.team_size ?? "-"}</p>
        ),
      },
      {
        accessorKey: "working_hours_start",
        header: "Working Hours",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <>
            {!original.working_hours_end && !original.working_hours_start ? (
              <p>-</p>
            ) : (
              <p>
                {original.working_hours_start ?? "-"} -{" "}
                {original.working_hours_end ?? "-"}{" "}
              </p>
            )}
          </>
        ),
      },
      {
        accessorKey: "working_days",
        header: "Working Days",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original.working_days ?? "-"} </p>
        ),
      },
      {
        accessorKey: "job_address",
        header: "Job Address",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>{original?.job_address?.name ?? "-"}</p>
        ),
      },
      {
        accessorKey: "interview_rounds",
        header: "Interview Stages",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <InterviewStagesView interview_rounds={original.interview_rounds} />
        ),
      },
      {
        accessorKey: "contacts",
        header: "Employer Hiring Contact",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <EmployerHiringView contacts={original.contacts} />
        ),
      },
      {
        accessorKey: "owner",
        header: "Assigned Recruiters",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <AssignedRecruitersView owner={original.owner} />
        ),
      },
      {
        accessorKey: "notes",
        header: "Notes",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p className="max-w-[100px] truncate">
            {original.notes[0]?.note ?? "-"}
          </p>
        ),
      },
      {
        accessorKey: "created_ts",
        header: "Created Date",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {original.created_ts
              ? dayjs(original.created_ts).format("DD MMM YYYY  ")
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "modified_ts",
        header: "Last Updated",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <p>
            {original.modified_ts
              ? dayjs(original.modified_ts).format("DD MMM YYYY")
              : "-"}
          </p>
        ),
      },
      {
        accessorKey: "file_url",
        header: "Job Description",
        cell: ({ row: { original } }: { row: { original: JobType } }) => (
          <>
            {original.file_url ? (
              <button
                onClick={() => {
                  window.open(original.file_url, "_blank");
                }}
              >
                <RhIcon icon="tabler:file" />
              </button>
            ) : (
              <p>-</p>
            )}
          </>
        ),
      },

      ...(filterJobsOn.status === "Draft"
        ? [
            {
              accessorKey: "is_draft",
              header: "Action",

              cell: ({ row: { original } }: { row: { original: JobType } }) => (
                <button>
                  <RhIcon
                    icon="tabler:pencil"
                    onClick={() =>
                      router.push(`/job-draft-edit?id=${original.id}`)
                    }
                  />
                </button>
              ),
            },
          ]
        : []),
    ],
    [filterJobsOn.status],
  );

  const pageChangeHandler = (changeToPage: number) => {
    setFilterJobsOn({
      ...filterJobsOn,
      page: changeToPage,
    });
  };

  return (
    <>
      <Head>
        {!openJobDrawerId ? (
          <title>Jobs | Zelevate</title>
        ) : (
          <title>Detail - Job | Zelevate</title>
        )}
      </Head>
      <div className="flex h-screen flex-col">
        <NavBar />

        <Header
          filterJobsOn={filterJobsOn}
          setFilterJobsOn={setFilterJobsOn}
          jobsCount={jobsCount}
        />

        <div className="background-blue flex flex-1 overflow-auto">
          <div className="relative flex flex-1 gap-tm2 overflow-auto px-[40px] pt-tm2 2xl:container 2xl:mx-auto">
            <div className="flex h-full w-[272px] flex-col">
              <JobFilter
                filterJobsOn={filterJobsOn}
                setFilterJobsOn={setFilterJobsOn}
                onAllFilterClear={onAllFilterClear}
              />
            </div>

            {/* table view */}
            <div className="flex h-full flex-1 flex-col overflow-scroll">
              <RhTable
                className="mx-0 h-full shadow-none [&_.table-body-td]:text-gray-900 [&_.table-head-th>div]:whitespace-nowrap"
                // isLoading={isAssessmentTemplatesLoading}
                columns={tableHeader}
                data={jobsList}
                styleOptions={["simple", "stickyHeader"]}
                manualPagination
                resultsPerPage={25}
              />
              {/* </div> */}
              <RhPagination
                resultsPerPage={25}
                totalResults={jobsCount ?? 0}
                onChange={pageChangeHandler}
                currentPage={filterJobsOn.page}
              />
            </div>
            {/* table view end */}
          </div>
        </div>
        <JobDetailsDrawer
          openJobDrawerId={openJobDrawerId}
          setOpenJobDrawerId={setOpenJobDrawerId}
        />
      </div>
    </>
  );
};

export default Jobs;

const isFilterValid = (filterJobOn: JobFilterParamType) => {
  if (filterJobOn.min_exp && filterJobOn.max_exp) {
    if (filterJobOn.min_exp >= filterJobOn.max_exp) return false;
  }

  if (filterJobOn.max_salary && filterJobOn.min_salary) {
    if (filterJobOn.min_salary >= filterJobOn.max_salary) return false;
  }
  return true;
};

// format filter params for api request
const formatFilterForApi = (filter: JobFilterParamType) => {
  const params = { ...filter };
  if (params.search) {
    params.search = params.search.toLowerCase();
  }

  if (params.status === "Draft") {
    params.is_draft = true;
    delete params.status;
  } else {
    params.is_draft = false;
  }

  if (params.job_role_details__selected) {
    params.job_role__id = params.job_role_details__selected.id?.toString();
    delete params.job_role_details__selected;
  }
  if (params.skills__selected) {
    if (params.skills__selected.length > 0) {
      params.skills = params.skills__selected
        .map((skill) => skill.id)
        .join(",");
    }
    delete params.skills__selected;
  }

  if (params.work_preference_new__selected) {
    params.work_preference_new = params.work_preference_new__selected.join(",");
    delete params.work_preference_new__selected;
  }

  if (params.city_location_new__selected) {
    if (params.city_location_new__selected.length > 0) {
      params.location_new__in = params.city_location_new__selected
        .map((city) => city.id)
        .join(",");
    }
    delete params.city_location_new__selected;
  }

  if (params.min_salary) {
    params.min_salary = params.min_salary * LAKH_CURRENCY_MULTIPLIER;
  }

  if (params.max_salary) {
    params.max_salary = params.max_salary * LAKH_CURRENCY_MULTIPLIER;
  }

  if (params.company__selected) {
    params.company__in = params.company__selected
      .map((company) => company.id)
      .join(",");
    delete params.company__selected;
  }

  if (params.owner__selected) {
    if (params.owner__selected.length > 0)
      params.owner = params.owner__selected.join(",");
    delete params.owner__selected;
  }
  return params;
};

const Header = ({
  filterJobsOn,
  setFilterJobsOn,
  jobsCount,
}: {
  filterJobsOn: JobFilterParamType;
  setFilterJobsOn: React.Dispatch<React.SetStateAction<JobFilterParamType>>;
  jobsCount: number | undefined;
}) => {
  const router = useRouter();

  const onSearchChangeHandler = (searchText: string) => {
    setFilterJobsOn((state) => {
      const { ...rest } = state;
      rest.page = 1;
      if (searchText === "") {
        delete rest.search;
        return rest;
      } else {
        rest.search = searchText;
        return rest;
      }
    });
  };

  return (
    <div className="flex items-center justify-between px-[40px] py-tpico 2xl:container 2xl:mx-auto">
      <div>
        <div
          className="flex items-center gap-tnano"
          data-testid="active-jobs-filter"
        >
          <h3>Jobs</h3>
        </div>
        {!!jobsCount && (
          <p className="body-small text-hint">
            {jobsCount >= 10000 ? "+10000" : jobsCount} records
          </p>
        )}
      </div>

      <div className="flex items-center gap-tm2">
        <div>
          <RhInputGroup className="flex gap-2 bg-white px-tmini">
            <DebouncedRhInput
              type={"text"}
              className="w-72"
              onChange={onSearchChangeHandler}
              placeholder="Search Job Title"
              value={filterJobsOn.search ?? ""}
            />
            <RhIcon className="text-hint icon-md" icon="tabler:search" />
          </RhInputGroup>
        </div>

        <Button
          data-testid="add-job-btn"
          onClick={() => router.push("/job-add")}
        >
          <RhIcon icon={"tabler:plus"} />
          Add Job
        </Button>
      </div>
    </div>
  );
};

const PreferredCityView = ({
  cities,
  work_type,
}: {
  cities: CityType[];
  work_type: LocationPreferenceEnum[] | null;
}) => {
  if (work_type?.includes("Remote") && (!cities || cities.length === 0)) {
    return <p>Remote</p>;
  }

  if (!cities || cities.length === 0) {
    return <p>-</p>;
  }

  return (
    <div className="flex items-center gap-tnano">
      <p className="whitespace-nowrap">
        {cities
          ?.slice(0, 2)
          .map((item) => item.name)
          .join(", ")}
      </p>

      {cities?.length > 2 && (
        <BoxTooltip.Root>
          <BoxTooltip.Target>
            <Chip size={"sm"} className="text-hint">
              +{cities.length - 2}
            </Chip>
          </BoxTooltip.Target>
          <BoxTooltip.Content>
            <p className="text-hint px-tsm py-tpico">Preferred City</p>
            <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
              {cities.slice(2).map((city) => (
                <Chip
                  key={city.id}
                  className="gap-tnano border-none"
                  variant={"blueFilled"}
                >
                  <p className="body-small text-gray-900">{city.name}</p>
                </Chip>
              ))}
            </div>
          </BoxTooltip.Content>
        </BoxTooltip.Root>
      )}
    </div>
  );
};

const PrioritizedSkills = ({
  priority_skills,
}: {
  priority_skills: JobPrioritySkillType;
}) => {
  if (
    !priority_skills.skills_in_priority ||
    priority_skills.skills_in_priority.length === 0
  )
    return <p>-</p>;
  else
    return (
      <div className="flex items-center gap-tnano">
        <div className="flex gap-tpico">
          {priority_skills.skills_in_priority
            .flat()
            ?.slice(0, 2)
            .map((skillInfo) => (
              <Chip size={"sm"} key={skillInfo.skill.id}>
                {skillInfo.skill.name}
              </Chip>
            ))}
        </div>

        {priority_skills.skills_in_priority.flat()?.length > 2 && (
          <BoxTooltip.Root>
            <BoxTooltip.Target>
              <Chip size={"sm"} className="text-hint">
                +{priority_skills.skills_in_priority.flat().length - 2}
              </Chip>
            </BoxTooltip.Target>
            <BoxTooltip.Content>
              <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
                {priority_skills.skills_in_priority
                  .flat()
                  .slice(2)
                  .map((skillInfo) => (
                    <Chip
                      key={skillInfo.skill.id}
                      size={"sm"}
                      className="gap-tnano border-none"
                    >
                      <p className="body-small text-gray-900">
                        {skillInfo.skill.name}
                      </p>
                    </Chip>
                  ))}
              </div>
            </BoxTooltip.Content>
          </BoxTooltip.Root>
        )}
      </div>
    );
};

const NonDealBreakderSkills = ({
  priority_skills,
}: {
  priority_skills: JobPrioritySkillType;
}) => {
  if (
    !priority_skills.non_deal_breaker_skills ||
    priority_skills.non_deal_breaker_skills.length === 0
  )
    return <p>-</p>;
  else
    return (
      <div className="flex items-center gap-tnano">
        <div className="flex gap-tpico">
          {priority_skills.non_deal_breaker_skills

            ?.slice(0, 2)
            .map((skillInfo) => (
              <Chip size={"sm"} key={skillInfo.skill.id}>
                {skillInfo.skill.name}
              </Chip>
            ))}
        </div>

        {priority_skills.non_deal_breaker_skills.flat()?.length > 2 && (
          <BoxTooltip.Root>
            <BoxTooltip.Target>
              <Chip size={"sm"} className="text-hint">
                +{priority_skills.non_deal_breaker_skills.flat().length - 2}
              </Chip>
            </BoxTooltip.Target>
            <BoxTooltip.Content>
              <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
                {priority_skills.non_deal_breaker_skills

                  .slice(2)
                  .map((skillInfo) => (
                    <Chip
                      key={skillInfo.skill.id}
                      size={"sm"}
                      className="gap-tnano border-none"
                    >
                      <p className="body-small text-gray-900">
                        {skillInfo.skill.name}
                      </p>
                    </Chip>
                  ))}
              </div>
            </BoxTooltip.Content>
          </BoxTooltip.Root>
        )}
      </div>
    );
};

const InterviewStagesView = ({
  interview_rounds,
}: {
  interview_rounds: InterviewStageType[] | undefined;
}) => {
  if (!interview_rounds || interview_rounds.length === 0) return <p>-</p>;
  else
    return (
      <div className="flex items-center gap-tnano">
        <div className="flex gap-tpico whitespace-nowrap">
          {interview_rounds?.slice(0, 2).map((item) => (
            <Chip size={"sm"} variant={"blueFilled"} key={item.id}>
              {item.interview_round.name}
            </Chip>
          ))
          // .join(", ")
          }
        </div>

        {interview_rounds?.length > 2 && (
          <BoxTooltip.Root>
            <BoxTooltip.Target>
              <Chip size={"sm"} className="text-hint">
                +{interview_rounds.length - 2}
              </Chip>
            </BoxTooltip.Target>
            <BoxTooltip.Content>
              <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
                {interview_rounds.slice(2).map((interview_round) => (
                  <Chip
                    key={interview_round.id}
                    className="gap-tnano border-none"
                    variant={"blueFilled"}
                  >
                    <p className="body-small text-gray-900">
                      {interview_round.interview_round.name}
                    </p>
                  </Chip>
                ))}
              </div>
            </BoxTooltip.Content>
          </BoxTooltip.Root>
        )}
      </div>
    );
};

const EmployerHiringView = ({
  contacts,
}: {
  contacts: ContactDetailsType[] | undefined;
}) => {
  if (!contacts || contacts.length === 0) return <p>-</p>;
  else
    return (
      <div className="flex items-center gap-tnano">
        <div className="flex gap-tpico whitespace-nowrap">
          {contacts?.slice(0, 2).map((item) => (
            <Chip size={"sm"} variant={"blueFilled"} key={item.id}>
              {item.name}
            </Chip>
          ))}
        </div>

        {contacts?.length > 2 && (
          <BoxTooltip.Root>
            <BoxTooltip.Target>
              <Chip size={"sm"} className="text-hint">
                +{contacts.length - 2}
              </Chip>
            </BoxTooltip.Target>
            <BoxTooltip.Content>
              <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
                {contacts.slice(2).map((contact) => (
                  <Chip
                    key={contact.id}
                    className="gap-tnano border-none"
                    variant={"blueFilled"}
                  >
                    <p className="body-small text-gray-900">{contact.name}</p>
                  </Chip>
                ))}
              </div>
            </BoxTooltip.Content>
          </BoxTooltip.Root>
        )}
      </div>
    );
};

const AssignedRecruitersView = ({
  owner,
}: {
  owner: OwnerDetailsType[] | undefined;
}) => {
  if (!owner || owner.length === 0) return <p>-</p>;
  else
    return (
      <div className="flex items-center gap-tnano">
        <div className="flex gap-tpico whitespace-nowrap">
          {owner?.slice(0, 2).map((item) => (
            <Chip size={"sm"} variant={"blueFilled"} key={item.id}>
              {item.name}
            </Chip>
          ))}
        </div>

        {owner?.length > 2 && (
          <BoxTooltip.Root>
            <BoxTooltip.Target>
              <Chip size={"sm"} className="text-hint">
                +{owner.length - 2}
              </Chip>
            </BoxTooltip.Target>
            <BoxTooltip.Content>
              <div className="mx-tsm flex max-w-[350px] flex-wrap items-center gap-tnano">
                {owner.slice(2).map((item) => (
                  <Chip
                    key={item.id}
                    className="gap-tnano border-none"
                    variant={"blueFilled"}
                  >
                    <p className="body-small text-gray-900">{item.name}</p>
                  </Chip>
                ))}
              </div>
            </BoxTooltip.Content>
          </BoxTooltip.Root>
        )}
      </div>
    );
};
